import { Marked, TextRenderer, Tokens } from 'marked';
import { escapeHtml } from './HtmlEscape';

/**
 * Markdown rendering for Propipe AI chat answers, which GroupChat shows with
 * v-html (EN-1350). marked has no sanitiser, so this renderer:
 * - shows raw HTML as text,
 * - renders only http and https links (target="_blank", rel="noopener
 *   noreferrer nofollow"); any other link shows just its label as text,
 * - never renders images, only their alt text.
 */

const REF_ID = /\[ref_id:[^\]]+\]/g;
const LINK_REL = 'noopener noreferrer nofollow';
/** Control characters and spaces the URL parser trims from both ends. */
// eslint-disable-next-line no-control-regex -- matching them is the point
const URL_EDGE_JUNK = /^[\u0000-\u0020]+|[\u0000-\u0020]+$/g;
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  colon: ':',
  tab: '\t',
  newline: '\n',
  nbsp: '\u00a0',
};

function decodeEntities(text: string): string {
  return text.replace(
    /&(#x[0-9a-f]+|#\d+|[a-z]+);?/gi,
    (entity, body: string) => {
      if (body[0] !== '#') return NAMED_ENTITIES[body.toLowerCase()] ?? entity;
      const code =
        body[1].toLowerCase() === 'x'
          ? parseInt(body.slice(2), 16)
          : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
    }
  );
}

/**
 * Returns the URL as the browser would resolve it if it is an absolute
 * http(s) URL, otherwise null. Entities are decoded first, as a browser does
 * for an href attribute; tabs and newlines are removed anywhere and other
 * control characters or spaces at the ends are trimmed, as the URL parser
 * does, so tricks like "java&#x09;script:" or a leading NUL can't hide the
 * scheme.
 */
export function safeHttpUrl(href: string | null | undefined): string | null {
  if (!href) return null;
  const decoded = decodeEntities(href)
    .replace(/[\t\n\r]/g, '')
    .replace(URL_EDGE_JUNK, '');
  if (!/^https?:\/\//i.test(decoded)) return null;
  try {
    const url = new URL(decoded);
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url.href
      : null;
  } catch {
    return null;
  }
}

const plainText = new TextRenderer();

const chatMarked = new Marked({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub-flavored markdown (tables, strikethrough, etc.)
  renderer: {
    html({ text, block }: Tokens.HTML | Tokens.Tag): string {
      if (!block) return escapeHtml(text);
      // Show a raw HTML block as a paragraph of text, line breaks kept, the
      // way the old pre-escaped input rendered.
      const lines = escapeHtml(text.replace(/\n+$/, '')).replace(/\n/g, '<br>');
      return `<p>${lines}</p>\n`;
    },
    link({ href, title, tokens }: Tokens.Link): string {
      const url = safeHttpUrl(href);
      if (!url)
        return escapeHtml(
          decodeEntities(this.parser.parseInline(tokens, plainText))
        );
      const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
      return `<a href="${escapeHtml(
        url
      )}"${titleAttr} target="_blank" rel="${LINK_REL}">${this.parser.parseInline(
        tokens
      )}</a>`;
    },
    image({ text }: Tokens.Image): string {
      return escapeHtml(decodeEntities(text));
    },
  },
});

/**
 * Renders a Propipe AI chat answer (markdown) to the HTML that GroupChat
 * shows with v-html.
 */
export function renderChatMarkdown(text: string): string {
  return chatMarked.parse(text.replace(REF_ID, ''), { async: false });
}
